import React, { useState, useCallback } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, Upload, File, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { FileUploadQuestion } from './questionTypes';
import { FileUploadAnswer } from './answerTypes';

interface FileUploadAdvancedProps {
  question: FileUploadQuestion;
  values?: FileUploadAnswer | null;
  onChange: (questionId: string, answer: FileUploadAnswer) => void;
}

const DOCUMENT_TYPES = [
  { value: "shareholder_agreement", label: "Shareholder Agreement" },
  { value: "corporate_bylaws", label: "Corporate Bylaws" },
  { value: "partnership_agreement", label: "Partnership Agreement" },
  { value: "operating_agreement", label: "Operating Agreement" },
  { value: "other", label: "Other" }
];

interface FileWithMetadata {
  file: File;
  documentType: string;
  isValid: boolean;
  error?: string;
}

export function FileUploadAdvanced({ question, values, onChange }: FileUploadAdvancedProps) {
  const [dragActive, setDragActive] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [filesWithMetadata, setFilesWithMetadata] = useState<FileWithMetadata[]>(() => {
    if (values?.files) {
      return values.files.map(f => ({
        file: { name: f.fileName, size: f.fileSize, type: f.fileType } as File,
        documentType: f.documentType || "other",
        isValid: f.isValid,
        error: f.isValid ? undefined : "File validation failed"
      }));
    }
    return [];
  });

  const validateFile = (file: File): { isValid: boolean; error?: string } => {
    // Check file type
    if (question.acceptedTypes && question.acceptedTypes.length > 0) {
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!question.acceptedTypes.includes(fileExtension)) {
        return { 
          isValid: false, 
          error: `File type not supported. Accepted types: ${question.acceptedTypes.join(', ')}` 
        };
      }
    }

    // Check file size (convert MB to bytes)
    const maxSize = (question.maxFileSize || 15) * 1024 * 1024;
    if (file.size > maxSize) {
      return { 
        isValid: false, 
        error: `File size must be less than ${question.maxFileSize || 15}MB` 
      };
    }

    return { isValid: true };
  };

  const handleFiles = (newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    const maxFiles = question.allowMultiple ? 10 : 1;
    const currentCount = filesWithMetadata.length;
    
    if (currentCount + fileArray.length > maxFiles) {
      setUploadErrors([`Maximum ${maxFiles} files allowed. You can upload ${maxFiles - currentCount} more files.`]);
      return;
    }

    const newFilesWithMetadata: FileWithMetadata[] = [];
    const errors: string[] = [];

    fileArray.forEach(file => {
      const validation = validateFile(file);
      newFilesWithMetadata.push({
        file,
        documentType: "other",
        isValid: validation.isValid,
        error: validation.error
      });
      
      if (validation.error) {
        errors.push(`${file.name}: ${validation.error}`);
      }
    });

    setUploadErrors(errors);
    const updatedFiles = [...filesWithMetadata, ...newFilesWithMetadata];
    setFilesWithMetadata(updatedFiles);
    updateAnswer(updatedFiles);
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const updateAnswer = (files: FileWithMetadata[]) => {
    const answer: FileUploadAnswer = {
      files: files.map(f => ({
        fileName: f.file.name,
        fileSize: f.file.size,
        fileType: f.file.type,
        documentType: f.documentType,
        uploadDate: new Date().toISOString(),
        isValid: f.isValid
      }))
    };
    onChange(question.id, answer);
  };

  const removeFile = (index: number) => {
    const updatedFiles = filesWithMetadata.filter((_, i) => i !== index);
    setFilesWithMetadata(updatedFiles);
    updateAnswer(updatedFiles);
  };

  const updateDocumentType = (index: number, documentType: string) => {
    const updatedFiles = [...filesWithMetadata];
    updatedFiles[index].documentType = documentType;
    setFilesWithMetadata(updatedFiles);
    updateAnswer(updatedFiles);
  };

  const clearErrors = () => {
    setUploadErrors([]);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">{question.title}</h2>
        {question.description && (
          <p className="text-muted-foreground mb-6">{question.description}</p>
        )}
      </div>

      <Card>
        <CardContent className="pt-6">
          {/* Display uploaded files */}
          {filesWithMetadata.length > 0 && (
            <div className="space-y-3 mb-6">
              {filesWithMetadata.map((fileData, index) => (
                <div key={index} className={`p-3 border rounded-md ${
                  fileData.isValid ? 'border-border bg-muted/50' : 'border-destructive bg-destructive/5'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <File className={`h-5 w-5 ${fileData.isValid ? 'text-muted-foreground' : 'text-destructive'}`} />
                      <div>
                        <p className="text-sm font-medium text-foreground">{fileData.file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(fileData.file.size)}
                        </p>
                        {!fileData.isValid && fileData.error && (
                          <p className="text-xs text-destructive mt-1">Not valid: {fileData.error}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  {fileData.isValid && (
                    <div className="mt-2">
                      <label className="text-xs font-medium text-muted-foreground block mb-1">
                        Document Type
                      </label>
                      <select
                        value={fileData.documentType}
                        onChange={(e) => updateDocumentType(index, e.target.value)}
                        className="w-full text-sm border border-border rounded px-2 py-1 bg-background"
                      >
                        {DOCUMENT_TYPES.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Error messages */}
          {uploadErrors.length > 0 && (
            <div className="space-y-2 mb-4">
              {uploadErrors.map((error, index) => (
                <div key={index} className="flex items-center space-x-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  <span>{error}</span>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={clearErrors}>
                Clear Errors
              </Button>
            </div>
          )}

          {/* File upload area */}
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
              dragActive 
                ? "border-primary bg-primary/5" 
                : "border-border hover:border-primary/50"
            )}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground mb-2">
              {question.uploadText || (question.allowMultiple ? "Upload your files" : "Upload your file")}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Drag and drop your {question.allowMultiple ? "files" : "file"} here, or click to browse
            </p>
            {question.allowMultiple && (
              <p className="text-xs text-muted-foreground mb-2">
                Maximum 10 files • Current: {filesWithMetadata.length}/10
              </p>
            )}
            <p className="text-xs text-muted-foreground mb-4">
              {question.acceptedTypes ? 
                `Accepted formats: ${question.acceptedTypes.join(", ")}` :
                "Accepted formats: PDF, DOC, DOCX"
              }
              • Max size: {question.maxFileSize || 15}MB per file
            </p>
            
            <input
              type="file"
              onChange={handleFileInputChange}
              accept={question.acceptedTypes?.join(",") || ".pdf,.doc,.docx"}
              multiple={question.allowMultiple}
              className="hidden"
              id={`file-upload-${question.id}`}
            />
            <label htmlFor={`file-upload-${question.id}`}>
              <Button type="button" variant="outline" className="cursor-pointer">
                Choose {question.allowMultiple ? "Files" : "File"}
              </Button>
            </label>
          </div>
          
          {!question.required && (
            <p className="text-xs text-muted-foreground mt-3 text-center">
              This file upload is optional
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}